import React, { useEffect, useState, useRef } from 'react';
import { APP_VERSION } from '../version';
import { Facebook, Instagram, Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { HostingsAPI } from '../api/hostings';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useLocation } from 'react-router-dom';
import { useTheme } from './ThemeProvider';

const Footer = () => {
  const { theme } = useTheme();
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const [plans, setPlans] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [token, setToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Determinar si mostrar la sección de contacto
  const showContactSection = ['/', '/comparar', '/complementos', '/hostings'].includes(location.pathname);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await HostingsAPI.list();
        if (res.success && res.data) {
          const activePlans = res.data
            .filter((p: any) => p.isVisible !== false && p.payType === 'ANUAL')
            .sort((a: any, b: any) => (a.price || 0) - (b.price || 0));
          setPlans(activePlans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    };
    fetchPlans();
  }, []);

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.message || !formData.service) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    if (!token) {
      alert('Por favor completa la verificación de seguridad');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('/api/send-contact-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, token })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', service: '', message: '' });
        turnstileRef.current?.reset();
        setToken(null);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  return (
    <footer className="flex flex-col">
      {/* Sección de Contacto */}
      {showContactSection && (
        <div className="bg-[#012654] text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col lg:flex-row">
              
              {/* Lado Izquierdo: Texto */}
              <div className="flex-1 p-10 lg:p-16 flex flex-col justify-center bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#012654] dark:text-white">
                  ¿Necesitas ayuda?
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                  Contáctanos, estamos aquí para ayudarte. Responderemos lo antes posible.
                </p>
                <div className="flex items-center gap-4 mt-8">
                  <div className="hidden min-[330px]:flex w-12 h-12 rounded-full bg-[#012654] dark:bg-blue-600 items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <a href="mailto:soporte@escalable.host" className="text-base min-[330px]:text-lg md:text-xl text-gray-800 dark:text-white hover:text-[#0170ba] dark:hover:text-blue-400 transition-colors font-medium break-all">
                    soporte@escalable.host
                  </a>
                </div>
              </div>

              {/* Lado Derecho: Formulario */}
              <div className="flex-1 p-10 lg:p-16 bg-white dark:bg-slate-800 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-100 dark:border-slate-700">
                {status === 'success' ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
                      <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">¡Mensaje Enviado!</h3>
                    <p className="text-gray-600 dark:text-gray-300">Gracias por contactarnos. Te responderemos a la brevedad.</p>
                    <button 
                      onClick={() => setStatus('idle')}
                      className="mt-8 px-6 py-2 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors"
                    >
                      Enviar otro mensaje
                    </button>
                  </div>
                ) : (
                  <div className="space-y-5 max-w-md mx-auto w-full">
                    <div>
                      <label htmlFor="service-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Servicio</label>
                      <select
                        id="service-select"
                        value={formData.service}
                        onChange={(e) => setFormData({...formData, service: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0170ba] dark:focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] dark:bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2360A5FA%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px_12px] bg-[right_1rem_center] bg-no-repeat"
                      >
                        <option value="" disabled>Selecciona 1</option>
                        <option value="otros">Otros servicios</option>
                        {plans.map((plan: any) => (
                          <option key={plan.id} value={plan.name}>
                            {plan.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Nombre *"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0170ba] dark:focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="email"
                          placeholder="Correo electrónico *"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0170ba] dark:focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <textarea
                        placeholder="Consulta *"
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0170ba] dark:focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      ></textarea>
                    </div>
                    
                    <div className="flex justify-center">
                      <div className="inline-block overflow-hidden rounded-[3px] border border-transparent dark:border-[#4a4a4a]">
                        <Turnstile
                          ref={turnstileRef}
                          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAA-aaaaaaaaaaaa'} 
                          onSuccess={setToken}
                          options={{
                            theme: theme === 'system' ? 'auto' : theme,
                            size: 'normal',
                          }}
                        />
                      </div>
                    </div>

                    {status === 'error' && (
                      <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                        <AlertCircle size={16} />
                        <span>Error al enviar el mensaje. Por favor intenta nuevamente.</span>
                      </div>
                    )}

                    <div className="pt-2 flex justify-center md:justify-end">
                      <button 
                        onClick={handleSubmit}
                        disabled={status === 'loading' || !token}
                        className="px-8 py-3 bg-[#0170ba] hover:bg-[#015a96] dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-bold rounded-full transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                      >
                        {status === 'loading' ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            ENVIANDO...
                          </>
                        ) : 'CONTACTAR'}
                      </button>
                    </div>
                  </div>
                )}
              </div>


            </div>
          </div>
        </div>
      )}

      {/* Franja Negra (Copyright) */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="text-center">
          {/* Layout para mobile: dos líneas separadas */}
          <div className="block sm:hidden space-y-2">
            {/* Primera línea: Copyright */}
            <p className="text-sm text-gray-300 font-normal">
              © Copyright 2020 - {currentYear}
            </p>

            {/* Segunda línea: Desarrollo */}
            <p className="text-sm text-gray-300 font-normal flex items-center justify-center gap-1">
              Desarrollo:
              <a
                href="https://adrianraineri.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center hover:opacity-80 transition-opacity duration-200"
              >
                <img
                  src="/assets/logoAR32x20.png"
                  alt="Logo Adrián Raineri"
                  width="32"
                  height="20"
                  className="h-5 w-auto inline-block"
                  style={{ marginTop: '-2px', marginRight: '-2px' }}
                />
                <span className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                  adrianraineri.com
                </span>
              </a>
            </p>
            {/* Versión */}
            <p className="text-xs text-gray-400 font-normal">
              {APP_VERSION}
            </p>
          </div>

          {/* Layout para desktop: una sola línea (como estaba antes) */}
          <p className="hidden sm:flex text-sm text-gray-300 font-normal items-center justify-center gap-1">
            © Copyright 2020 - {currentYear} | Desarrollo:
            <a
              href="https://adrianraineri.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center hover:opacity-80 transition-opacity duration-200"
            >
              <img
                src="/assets/logoAR32x20.png"
                alt="Logo Adrián Raineri"
                width="32"
                height="20"
                className="h-5 w-auto inline-block"
                style={{ marginTop: '-2px', marginRight: '-2px' }}
              />
              <span className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                adrianraineri.com
              </span>
            </a>
          </p>
          {/* Versión en desktop */}
          <p className="hidden sm:block text-xs text-gray-400 font-normal mt-1">
            {APP_VERSION}
          </p>
        </div>
      </div>
      </div>
    </footer>
  );
};

export default Footer;
